import type { Transaction, TransactionAllocation } from '../types';

export type AllocationFulfillmentStatus =
  | 'completed'
  | 'partial'
  | 'failed'
  | 'refunded'
  | 'pending'
  | 'rejected';

export type AllocationFulfillmentSummary = {
  fulfillmentStatus: AllocationFulfillmentStatus | null;
  total: number;
  completed: number;
  pending: number;
  failed: number;
  refunded: number;
  rejected: number;
  syncStepReason: string | null;
  failureMessages: string[];
};

function normalizeAllocationStatus(alloc: TransactionAllocation): string {
  return String(alloc.displayStatus || alloc.status || 'pending').toLowerCase();
}

/** Multi-game parent rows need GET /admin/transactions/:id/allocations for per-game status. */
export function transactionNeedsAllocationsFetch(tx: Transaction): boolean {
  if (tx.type !== 'deposit') return false;
  if (tx.hasChildAllocations) return true;
  const meta = tx.metadata ?? {};
  const sync = tx.syncMetadata ?? {};
  if (meta.isMultiGame === true || sync.isMultiGame === true) return true;
  if (Array.isArray(meta.allocations) && meta.allocations.length > 0) return true;
  if (Array.isArray(sync.allocations) && sync.allocations.length > 0) return true;
  return false;
}

/**
 * Derive fulfillment status from allocation rows (mirrors backend buildDepositFulfillment when parent is completed).
 */
export function summarizeTransactionAllocations(
  allocations: TransactionAllocation[],
  parentStatus: string,
): AllocationFulfillmentSummary {
  const empty: AllocationFulfillmentSummary = {
    fulfillmentStatus: null,
    total: 0,
    completed: 0,
    pending: 0,
    failed: 0,
    refunded: 0,
    rejected: 0,
    syncStepReason: null,
    failureMessages: [],
  };

  if (!allocations.length) return empty;

  const counts = { completed: 0, pending: 0, failed: 0, refunded: 0, rejected: 0 };
  const failureMessages: string[] = [];

  for (const alloc of allocations) {
    const s = normalizeAllocationStatus(alloc);
    if (s === 'completed') counts.completed += 1;
    else if (s === 'refunded') counts.refunded += 1;
    else if (s === 'rejected') counts.rejected += 1;
    else if (s === 'failed' || s === 'cancelled' || s === 'expired') counts.failed += 1;
    else counts.pending += 1;

    if (alloc.errorMessage && (s === 'failed' || s === 'refunded' || s === 'rejected')) {
      const game =
        alloc.gameProviderName || alloc.gameProviderCode || alloc.gameProviderId?.slice(0, 8) || 'Game';
      failureMessages.push(`${game}: ${alloc.errorMessage.trim()}`);
    }
  }

  const total = allocations.length;
  const parent = String(parentStatus || '').toLowerCase();
  let fulfillmentStatus: AllocationFulfillmentStatus | null = null;

  const pendingParent = ['pending', 'processing', 'retry_scheduled', 'awaiting_admin', 'approved'].includes(
    parent,
  );

  if (pendingParent) {
    fulfillmentStatus = 'pending';
  } else if (parent === 'completed') {
    if (counts.completed === total) fulfillmentStatus = 'completed';
    else if (counts.pending === total) fulfillmentStatus = 'pending';
    else if (counts.refunded === total) fulfillmentStatus = 'refunded';
    else if (counts.rejected === total) fulfillmentStatus = 'rejected';
    else if (counts.failed === total) fulfillmentStatus = 'failed';
    else fulfillmentStatus = 'partial';
  } else if (parent === 'rejected') {
    fulfillmentStatus = 'rejected';
  } else if (parent === 'failed' || parent === 'cancelled' || parent === 'expired') {
    fulfillmentStatus = parent as AllocationFulfillmentStatus;
  }

  const failedTotal = counts.failed + counts.refunded + counts.rejected;
  let syncStepReason: string | null = null;
  if (failureMessages.length) {
    syncStepReason = failureMessages.slice(0, 2).join(' · ');
    if (failureMessages.length > 2) syncStepReason += ` · +${failureMessages.length - 2} more`;
  } else if (fulfillmentStatus === 'partial') {
    syncStepReason = `${counts.completed}/${total} games synced · ${failedTotal} failed`;
  }

  return {
    fulfillmentStatus,
    total,
    ...counts,
    syncStepReason,
    failureMessages,
  };
}

/** High-level sync step note for partial fulfillment (no per-game error dump). */
export function formatPartialSyncStepReason(summary: AllocationFulfillmentSummary): string {
  const failedTotal = summary.failed + summary.refunded + summary.rejected;
  let reason = `${summary.completed}/${summary.total} games synced`;
  if (failedTotal > 0) {
    reason += summary.refunded > 0 ? ` · ${failedTotal} refunded` : ` · ${failedTotal} failed`;
  }
  if (summary.pending > 0) reason += ` · ${summary.pending} pending`;
  return reason;
}

export function formatPartialCompletedStepReason(): string {
  return 'Partial fulfillment — not all games credited';
}
